import { FC } from 'react';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { DeleteIcon, EditIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { Text } from '../components/Text';
import { Menu } from '../components/Menu';
import { MemberAvatar } from './MemberAvatar';
import { useDeleteHouseholdMember } from './useDeleteHouseholdMember';
import { ContactMethodsList } from '../contact-methods/ContactMethodsList';
import { NameHelper } from '../contact-methods/NameHelper';
import { MemberAccessBanner } from '../share-caremap/sharing-and-access/MemberAccessBanner';
import { CareMapNotesList } from '../notes/note-list/CareMapNotesList';
import { NudgeBadge } from '../components/NudgeBadge';
import { DrawerListSection } from '../components/DrawerListSection';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { PrimaryCaregiverBadge } from './PrimaryCaregiverBadge';

export interface IHouseholdMemberViewProps {
    memberId: string;
};

export const HouseholdMemberView: FC<IHouseholdMemberViewProps> = ({ memberId }) => {
    const nameHelper = useInstance(NameHelper);

    const careMapStateInstance = useInstance(CareMapState);
    useSubscription(() => careMapStateInstance);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const canEdit = careMapStateInstance.hasPermissionTo('put');

    const member = careMapStateInstance.maybeGetHouseholdMember(memberId);
    const history = useInstance(HistoryViewModel);

    const deleteMember = useDeleteHouseholdMember({ memberId, onDelete: () => {
        // assume household member list is back
        history.back();
    }});

    if (!careMapStateInstance.state || !member) {
        return null;
    }

    const isOwnRecord = careMapStateInstance.isMyMember(member);

    const editMember = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/household/member/${member.id}/edit`);
    };

    const viewMoreNotes = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/household/member/${memberId}/notes`);
    };

    const editNote = (noteId: string) => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/household/member/${memberId}/notes/${noteId}`);
    };

    const addNote = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/household/member/${memberId}/notes/add-note`);
    };

    const showEditNudgeBadge = canEdit && !member.contactMethods.length && !member.address;

    const notes = notesFactory.forActiveCaremap().getNotesForTarget({ section: 'household', memberId });

    return (
        <CareMapDrawerPage title="Inner Circle Member Detail" hasBack>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: 20 }}>
                    <MemberAvatar member={member} />

                    <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 4 }}>
                        <Text type="title" data-type="title"><span className="mp-mask">{member.firstName || ''} {member.lastName || ''}{isOwnRecord ? <span style={{ fontWeight: 'normal' }}> (you)</span> : ''}</span></Text>
                        <Text type="subtitle" data-type="subtitle">{member.relationship || ''}</Text>
                        <div style={{display: 'flex', marginTop: 4}}>
                            <PrimaryCaregiverBadge primaryCaregiver={member.primaryCaregiver} />
                        </div>
                    </div>

                    {canEdit &&
                        <div style={{marginTop: -8}}>
                            <Menu actions={[
                                { Icon: EditIcon, text: 'Edit Inner Circle Member', handler: editMember },
                                { Icon: DeleteIcon, text: `Delete Inner Circle Member`, role: 'destructive', handler: deleteMember }
                            ]} />
                        </div>
                    }


                </div>

                {showEditNudgeBadge &&
                    <NudgeBadge
                        buttonLabel="Complete Profile"
                        buttonClickHandler={editMember}
                        message="Be sure to add address and contact info to help you stay organized"
                    />
                }

                <MemberAccessBanner
                    memberId={memberId}
                    email={careMapStateInstance.getContactMethodOrDefault(member, 'email').value}
                    linkedUserId={member.linkedUserId}
                />

                <ContactMethodsList
                    contactMethods={member.contactMethods}
                    address={member.address}
                    iconWidth={64}
                    emailToName={nameHelper.getName(member)}
                    trackingContext={{ category: 'household', relationship: member.relationshipId || member.relationship }}
                />

                <DrawerListSection>
                    <CareMapNotesList
                        showCount={3}
                        onNoteClicked={editNote}
                        onAddNote={addNote}
                        noteTarget={{ section: 'household', memberId }}
                        moreNotesHandler={viewMoreNotes}
                        notes={notes}
                    />
                </DrawerListSection>
            </div>

        </CareMapDrawerPage>
    );
};
